import { SignUpGuardProvider } from '@app/hooks/SignUpGuardContext';
import { useEngageTextMessaging } from '@app/hooks/useEngageTextMessaging';
import Custom404 from '@app/pages/404.page';
import { MAIN_SECTION_ID } from '@app/pages/people/[artistUsername]/constants';
import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import PageSection from '@redbubble/design-system/src/PageSection';
import { RedbubbleLayout } from '@redbubble/next/app';
import { AppProps, RBLayoutProps } from '@redbubble/next/types';
import { usePageProps } from '@redbubble/next-provider';
import dynamic from 'next/dynamic';
import { PropsWithChildren } from 'react';
import styles from './ArtistPageLayout.module.scss';
import { ArtistHeader } from './components/ArtistHeader';
import { ArtistProfile } from './components/ArtistProfile';
import { ArtistShopNavTabs } from './components/ArtistShopNavTabs';
import { ArtistPageProps, ArtistSpecificPageProps } from './types';

const ReactToastContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReactToastContainer" */
      '@app/components/Toasts/ReactToastContainer'
    ).then(({ ReactToastContainer: Component }) => Component),
  { ssr: false, loading: () => null }
);

const SignUpGuard = dynamic(
  () =>
    import(
      /* webpackChunkName: "SignUpGuard" */
      '@app/components/SignUpGuard/SignUpGuard'
    ).then(({ SignUpGuard: Component }) => Component),
  { ssr: false, loading: () => null }
);

const ArtistPageInnerLayout = ({ children }: PropsWithChildren) => {
  const { artistInfo, badgeStatuses, isFollowing, userInfo } = usePageProps<
    AppProps & ArtistPageProps<ArtistSpecificPageProps>
  >();

  // In commonProps we respond code 410 but the 404 component is fine to display. This must be directly
  // following the usePageProps hook so we can catch early on if we short-circuited the props with {}.
  if (!artistInfo) return <Custom404 userInfo={userInfo} />;

  return (
    <SignUpGuardProvider>
      <SignUpGuard />
      <PageSection size={constants.MEDIUM}>
        <ArtistHeader artistInfo={artistInfo} />
        <ArtistShopNavTabs />
        <Box
          element="main"
          id={MAIN_SECTION_ID}
          className={styles.primaryContent}
        >
          {children}
        </Box>
        <ArtistProfile
          artistInfo={artistInfo}
          badgeStatuses={badgeStatuses}
          isFollowing={isFollowing}
        />
        <ReactToastContainer />
      </PageSection>
    </SignUpGuardProvider>
  );
};

const ArtistPageLayout = (props: RBLayoutProps) => {
  const engagingPlaceholderMessages = useEngageTextMessaging();

  return RedbubbleLayout({
    ...props,
    children: <ArtistPageInnerLayout {...props} />,
    engagingPlaceholderMessages,
  });
};

export default ArtistPageLayout;
